import { ref, watch, type Ref } from 'vue';
import QRCode from 'qrcode';

type UseQrCodeReturn = {
    qrCodeDataUrl: Ref<string>;
    isGenerating: Ref<boolean>;
    errorMessage: Ref<string>;
    generate: (value: string) => Promise<void>;
};

/**
 * QRコード生成のカスタムフック
 * @param initialValue - 初期値（オプション）
 * @returns QRコードDataURL、生成中フラグ、エラー、生成関数
 */
export const useQrCode = (initialValue?: Ref<string> | string): UseQrCodeReturn => {
    const qrCodeDataUrl = ref<string>('');
    const isGenerating = ref(false);
    const errorMessage = ref<string>('');

    const generate = async (value: string): Promise<void> => {
        if (!value) {
            qrCodeDataUrl.value = '';
            return;
        }

        try {
            isGenerating.value = true;
            errorMessage.value = '';

            const options = {
                width: 200,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF',
                },
            };

            qrCodeDataUrl.value = await QRCode.toDataURL(value, options);
        } catch {
            errorMessage.value = 'QRコードの生成に失敗しました';
            qrCodeDataUrl.value = '';
        } finally {
            isGenerating.value = false;
        }
    };

    // 初期値がRefの場合は監視して自動生成
    if (initialValue !== undefined) {
        if (typeof initialValue === 'string') {
            generate(initialValue);
        } else {
            watch(
                initialValue,
                (newValue) => {
                    if (newValue) {
                        generate(newValue);
                    }
                },
                { immediate: true }
            );
        }
    }

    return {
        qrCodeDataUrl,
        isGenerating,
        errorMessage,
        generate,
    };
};
